import React, { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, LogOut, BarChart3, Plus, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useClientContext } from '@/contexts/ClientContext';
import { usePageTransition } from '@/hooks/usePageTransition';
import { PageLoading } from '@/components/ui/page-loading';
import { UserPortalForm } from '@/components/UserPortal/UserPortalForm';
import UserPortalDashboard from '@/components/UserPortal/UserPortalDashboard';
import { UserTicketsList } from '@/components/UserPortal/UserTicketsList';
import { KnowledgeBaseSearch } from '@/components/UserPortal/KnowledgeBaseSearch';
import { NovaRequisicaoModal } from '@/components/UserPortal/NovaRequisicaoModal';
import { UserProfileEdit } from '@/components/UserPortal/UserProfileEdit';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { UserFormulariosList } from '@/components/UserPortal/UserFormulariosList';
import { useUserFormularios } from '@/hooks/useUserFormularios';
import { FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const UserPortal = () => {
  const { signOut, profile } = useAuth();
  const { currentClientName, loading: clientLoading } = useClientContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isNovaRequisicaoModalOpen, setIsNovaRequisicaoModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { isLoading, loadingMessage, startTransition, endTransition } = usePageTransition();
  const [profileData, setProfileData] = useState<any>(null);
  const { totalPendentes } = useUserFormularios();

  // Removido: Log de role do usuário logado (apenas este!)
  // if (profile) {
  //   console.log('[UserPortal] Usuário logado:', profile);
  // }

  // Buscar dados do perfil para o avatar
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!profile?.id) return;
      
      try {
        const { data } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', profile.id)
          .single();
        
        if (data) {
          setProfileData(data);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [profile?.id]);

  // Atualizar: garantir redirecionamento para /auth após logout
  const handleSignOut = async () => {
    startTransition('Fazendo logout...');
    await signOut();
    navigate("/auth", { replace: true });
  };

  const handleNovaRequisicaoClick = () => {
    setIsNovaRequisicaoModalOpen(true);
  };

  const handleProfileClick = () => {
    setIsProfileModalOpen(true);
  };

  const handleAdminClick = () => {
    startTransition('Acessando área administrativa...');
    navigate('/dashboard');
  };

  const handleTabChange = (value: string) => {
    startTransition('Carregando...');
    setActiveTab(value);
    endTransition();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 animate-fade-in relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-no-repeat bg-right bg-contain opacity-40 pointer-events-none z-0"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundPosition: 'right center',
          backgroundSize: 'auto 100%'
        }}
      />
      {/* Header moderno */}
      <header className="glass-effect sticky top-0 z-20 transition-all duration-300 relative border-b-0">
        <div className="w-full px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110">
                <img src="/logos/logo_Aruan_header.png" alt="Aruan Logo" className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Portal de Auto Atendimento
                </h1>
                <p className="text-xs text-slate-600 font-medium">Sistema de Atendimento</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Avatar 
                className="h-10 w-10 ring-2 ring-blue-200 cursor-pointer hover:ring-blue-400 transition-all duration-300 hover:scale-110 hover:shadow-lg"
                onClick={handleProfileClick}
              >
                <AvatarImage 
                  src={(profileData as any)?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.full_name || 'User')}&background=random`} 
                />
                <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-semibold font-display">
                  {profile?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-slate-900">
                  {profile?.full_name || 'Usuário'}
                </p>
                <p className="text-xs text-slate-600 font-medium">{currentClientName || 'Cliente'}</p>
              </div>
              
              {/* Link para Administração - apenas para admin ou client_admin */}
              {(profile?.role === 'admin' || profile?.role === 'client_admin' || profile?.role === 'technician') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAdminClick}
                  className="flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all duration-300 hover:shadow-md"
                >
                  <Settings className="h-4 w-4" />
                  <span className="hidden sm:inline">Administração</span>
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut} 
                className="flex items-center gap-2 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-300 hover:shadow-md"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-2 sm:px-4 lg:px-6 py-8 animate-fade-in relative z-10" style={{ '--tw-animation-delay': '0.1s' } as React.CSSProperties}>
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in" style={{ '--tw-animation-delay': '0.2s' } as React.CSSProperties}>
          <div>
            <h2 className="text-4xl font-display gradient-heading mb-2">
              Bem-vindo ao seu Portal
            </h2>
            <p className="text-lg text-slate-600 font-medium">
              Gerencie suas requisições e acompanhe o status dos seus chamados
            </p>
          </div>
          
          <Button
            onClick={handleNovaRequisicaoClick}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-2xl transition-all duration-300 flex items-center gap-2 px-8 py-6 text-base font-semibold hover:scale-105 ripple-effect"
          >
            <Plus className="h-5 w-5" />
            Nova Requisição
          </Button>
        </div>

        {/* Ações Rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-8 animate-fade-in" style={{ '--tw-animation-delay': '0.3s' } as React.CSSProperties}>
          <div 
            onClick={handleNovaRequisicaoClick}
            className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 border-2 border-blue-200 rounded-2xl p-6 cursor-pointer card-lift group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl group-hover:shadow-lg transition-all duration-300 group-hover:scale-110">
                  <Plus className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="font-display font-bold text-lg text-blue-900 mb-2">Nova Requisição</h3>
              <p className="text-sm text-blue-700 font-medium">Abrir uma nova solicitação de serviço</p>
            </div>
          </div>

          <div 
            onClick={() => handleTabChange('tickets')}
            className="bg-gradient-to-br from-emerald-50 via-emerald-50 to-emerald-100 border-2 border-emerald-200 rounded-2xl p-6 cursor-pointer card-lift group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-600/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl group-hover:shadow-lg transition-all duration-300 group-hover:scale-110">
                  <BarChart3 className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="font-display font-bold text-lg text-emerald-900 mb-2">Meus Tickets</h3>
              <p className="text-sm text-emerald-700 font-medium">Acompanhar status das solicitações</p>
            </div>
          </div>

          <div 
            onClick={() => handleTabChange('knowledge')}
            className="bg-gradient-to-br from-violet-50 via-violet-50 to-violet-100 border-2 border-violet-200 rounded-2xl p-6 cursor-pointer card-lift group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-violet-600 to-violet-700 rounded-xl group-hover:shadow-lg transition-all duration-300 group-hover:scale-110">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="font-display font-bold text-lg text-violet-900 mb-2">Base de Conhecimento</h3>
              <p className="text-sm text-violet-700 font-medium">Buscar soluções e tutoriais</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-8 animate-fade-in" style={{ '--tw-animation-delay': '0.4s' } as React.CSSProperties}>
          <TabsList className="grid w-full grid-cols-4 glass-effect rounded-2xl overflow-hidden p-1">
            <TabsTrigger 
              value="dashboard" 
              className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg py-3 rounded-xl transition-all duration-300 font-semibold"
            >
              <BarChart3 className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger 
              value="tickets" 
              className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg py-3 rounded-xl transition-all duration-300 font-semibold"
            >
              <AlertTriangle className="h-4 w-4" />
              Meus Tickets
            </TabsTrigger>
            <TabsTrigger 
              value="formularios" 
              className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg py-3 rounded-xl transition-all duration-300 font-semibold relative"
            >
              <FileText className="h-4 w-4" />
              Formulários
              {totalPendentes > 0 && (
                <Badge className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs bg-red-500 hover:bg-red-500 animate-bounce-subtle">
                  {totalPendentes}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="knowledge" 
              className="flex items-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg py-3 rounded-xl transition-all duration-300 font-semibold"
            >
              <Settings className="h-4 w-4" />
              Ajuda
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <UserPortalDashboard />
          </TabsContent>

          <TabsContent value="tickets" className="space-y-6">
            <UserTicketsList />
          </TabsContent>

          <TabsContent value="formularios" className="space-y-6">
            <UserFormulariosList />
          </TabsContent>

          <TabsContent value="knowledge" className="space-y-6">
            <KnowledgeBaseSearch />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal de Nova Requisição */}
      <NovaRequisicaoModal 
        isOpen={isNovaRequisicaoModalOpen}
        onClose={() => setIsNovaRequisicaoModalOpen(false)}
      />

      {/* Modal de Perfil */}
      <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Perfil</DialogTitle>
          </DialogHeader>
          <UserProfileEdit />
        </DialogContent>
      </Dialog>

      {/* Loading Screen */}
      {isLoading && <PageLoading message={loadingMessage} />}
    </div>
  );
};

export default UserPortal;
